import {EventEmitter, Injectable, Output} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {CFEDTO} from '../../models/dtos/CFEDTO';
import {Observable, ReplaySubject} from 'rxjs';
import {RespuestaEmisionCFEDTO} from '../../models/dtos/RespuestaEmisionCFEDTO';
import { environment } from '../../environments/environment';
import {FiltroObtenerCFEsDTO} from '../../models/dtos/filters/FiltroObtenerCFEsDTO';
import {CommunicationService} from './communication/communication.service';
import {RespuestaAnulacionCFEDTO} from '../../models/dtos/RespuestaAnulacionCFEDTO';
import {DatosAnulacionCFEDTO} from '../../models/dtos/DatosAnulacionCFEDTO';
import {EmisorCFEDTO} from '../../models/dtos/EmisorCFEDTO';
import {ConfiguracionInicialDTO} from '../../models/dtos/ConfiguracionInicialDTO';
import {ReceptorCFEDTO} from '../../models/dtos/ReceptorCFEDTO';
import {RespuestaReceptorCFEDTO} from '../../models/dtos/RespuestaReceptorCFEDTO';
import {ParamEmisionCFEResolverDTO} from '../../models/dtos/ParamEmisionCFEResolverDTO';
import {DatosInicioEmisionCFEDTO} from '../../models/dtos/datosInicio/DatosInicioEmisionCFEDTO';
import {Utils} from '../utils/utils';
import { CFEPageDTO } from 'src/models/dtos/CFEPageDTO';
import { saveAs } from 'file-saver';

@Injectable
({
  providedIn: 'root'
})
export class EmisionCfeService
{
  apiBaseUrl: string = environment.apiBaseUrl;

  private emisorCFEDTO: EmisorCFEDTO;

  @Output()
  cambioEnEmisorCFEDTO: EventEmitter<EmisorCFEDTO> = new EventEmitter<EmisorCFEDTO>();

  private configInicialDTO: ConfiguracionInicialDTO;

  constructor(private communicationService: CommunicationService, private http: HttpClient)
  {

  }

  public emitirCFE(cfeDTO: CFEDTO): Observable<RespuestaEmisionCFEDTO>
  {
    const urlStr: string = this.apiBaseUrl + '/cfes';

    return this.communicationService.doPost(urlStr, cfeDTO, null);
  }

  public obtenerCFEs(filtroObtenerCFEsDTO: FiltroObtenerCFEsDTO): Observable<CFEPageDTO>
  {
    const urlStr: string = this.apiBaseUrl + '/cfes';

    let httpParams = new HttpParams();
    httpParams = httpParams.set('rutEmisor', filtroObtenerCFEsDTO.rutEmisor);
    httpParams = httpParams.set('cfeType', filtroObtenerCFEsDTO.cfeType ? filtroObtenerCFEsDTO.cfeType.toString() : '');
    httpParams = httpParams.set('initialDate', filtroObtenerCFEsDTO.initialDate ? filtroObtenerCFEsDTO.initialDate.toISOString() : '');
    httpParams = httpParams.set('finalDate', filtroObtenerCFEsDTO.finalDate ? filtroObtenerCFEsDTO.finalDate.toISOString() : '');
    httpParams = httpParams.set('documentosReceptores', filtroObtenerCFEsDTO.documentosReceptores);
    httpParams = httpParams.set('razonSocialReceptores', filtroObtenerCFEsDTO.razonSocialReceptores);
    httpParams = httpParams.set('pageIndex', filtroObtenerCFEsDTO.pageIndex.toString());
    httpParams = httpParams.set('pageSize', filtroObtenerCFEsDTO.pageSize.toString());

    return this.communicationService.doGet(urlStr, null, httpParams, null);
  }

  public descargarImpresionPDF(codigoTipoCFE: number, serie: string, numero: number, rutEmisor: string, fechaDesde: string, fechaAudit: string): Observable<Blob>
  {
    const urlStr: string = this.apiBaseUrl + '/impresionesPDFs';

    let httpParams = new HttpParams();
    httpParams = httpParams.set('codigoTipoCFE', String(codigoTipoCFE));
    httpParams = httpParams.set('serie', serie);
    httpParams = httpParams.set('numero', String(numero));
    httpParams = httpParams.set('rutEmisor', rutEmisor);
    httpParams = httpParams.set('fechaDesde', fechaDesde);
    httpParams = httpParams.set('fechaAudit', fechaAudit);

    const responseType: any = 'arraybuffer';

    return this.communicationService.doGet(urlStr, null, httpParams, responseType);
  }

  public anularCFE(datosAnulacionCFEDTO: DatosAnulacionCFEDTO): Observable<RespuestaAnulacionCFEDTO>
  {
    const urlStr: string = this.apiBaseUrl + '/anulacionesCfes';

    return this.communicationService.doPost(urlStr, datosAnulacionCFEDTO, null);
  }

  public obtenerEmisorCFE(email: string, rut: string): Observable<Array<EmisorCFEDTO>>
  {
    const urlStr: string = this.apiBaseUrl + '/emisores';
    let httpParams: HttpParams = new HttpParams();

    if (Utils.isNotNullOrUndefined(email))
    {
      httpParams = httpParams.set('email', email);
    }

    if (Utils.isNotNullOrUndefined(rut))
    {
      httpParams = httpParams.set('rut', rut);
    }

    return this.communicationService.doGet(urlStr, null, httpParams, null);
  }

  public obtenerConfiguracionInicial(): Observable<ConfiguracionInicialDTO>
  {
    const urlStr: string = this.apiBaseUrl + '/confsIniciales';

    return this.communicationService.doGet(urlStr, null, null, null);
  }

  public obtenerReceptoresCFE(email: string): Observable<Array<ReceptorCFEDTO>>
  {
    const urlStr: string = this.apiBaseUrl + '/receptores';
    let httpParams = new HttpParams();
    httpParams = httpParams.set('email', email);

    return this.communicationService.doGet(urlStr, null, httpParams, null);
  }

  public agregarReceptorCFE(email: string, receptorCFEDTO: ReceptorCFEDTO): Observable<RespuestaReceptorCFEDTO>
  {
    const urlStr: string = this.apiBaseUrl + '/receptores';
    let httpParams = new HttpParams();
    httpParams = httpParams.set('email', email);

    return this.communicationService.doPost(urlStr, receptorCFEDTO, httpParams);
  }

  public modificarReceptorCFE(receptorCFEDTO: ReceptorCFEDTO): Observable<RespuestaReceptorCFEDTO>
  {
    const urlStr: string = this.apiBaseUrl + '/receptores/' + receptorCFEDTO.codigo.toString();

    return this.communicationService.doPatch(urlStr, receptorCFEDTO, null, null);
  }

  public eliminarReceptorCFE(codigoReceptor: number): Observable<RespuestaReceptorCFEDTO>
  {
    const urlStr: string = this.apiBaseUrl + '/receptores/' + codigoReceptor.toString();

    return this.communicationService.doDelete(urlStr, null, null);
  }

  public getEmisorCFEDTO(): EmisorCFEDTO
  {
    return this.emisorCFEDTO;
  }

  public setEmisorCFEDTO(emisorCFEDTO: EmisorCFEDTO)
  {
    this.emisorCFEDTO = emisorCFEDTO;
    this.cambioEnEmisorCFEDTO.emit(emisorCFEDTO);
  }

  public getConfiguracionInicialDTO()
  {
    return this.configInicialDTO;
  }

  public setConfiguracionInicialDTO(configInicialDTO: ConfiguracionInicialDTO)
  {
    this.configInicialDTO = configInicialDTO;
  }

  public obtenerConfiguracionInicialEmisionCFE(paramEmisionCFEResolverDTO: ParamEmisionCFEResolverDTO): Observable<DatosInicioEmisionCFEDTO>
  {
    const urlStr: string = this.apiBaseUrl + '/incioCfe';

    return this.communicationService.doPost(urlStr, paramEmisionCFEResolverDTO, null);
  }

  public salvarEmisorCFE(emisorCFEDTO: EmisorCFEDTO): Observable<EmisorCFEDTO>
  {
    const urlStr: string = this.apiBaseUrl + '/emisores';

    return this.communicationService.doPost(urlStr, emisorCFEDTO, null);
  }

  exportToExcel(filtroObtenerCFEsDTO: FiltroObtenerCFEsDTO): void {
    const urlStr: string = this.apiBaseUrl + '/exportarCfes';

    const headers = new HttpHeaders({
      'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    let httpParams = new HttpParams();
    httpParams = httpParams.set('rutEmisor', filtroObtenerCFEsDTO.rutEmisor);
    httpParams = httpParams.set('cfeType', filtroObtenerCFEsDTO.cfeType ? filtroObtenerCFEsDTO.cfeType.toString() : '');
    httpParams = httpParams.set('initialDate', filtroObtenerCFEsDTO.initialDate ? filtroObtenerCFEsDTO.initialDate.toISOString() : '');
    httpParams = httpParams.set('finalDate', filtroObtenerCFEsDTO.finalDate ? filtroObtenerCFEsDTO.finalDate.toISOString() : '');
    httpParams = httpParams.set('documentosReceptores', filtroObtenerCFEsDTO.documentosReceptores);
    httpParams = httpParams.set('razonSocialReceptores', filtroObtenerCFEsDTO.razonSocialReceptores);
    httpParams = httpParams.set('pageIndex', filtroObtenerCFEsDTO.pageIndex.toString());
    httpParams = httpParams.set('pageSize', filtroObtenerCFEsDTO.pageSize.toString());

    //this.communicationService.doGet(urlStr, null, httpParams, null);

    this.http.get(urlStr,
      { responseType: 'blob',
        headers: headers,
        params: httpParams })
      .subscribe((data: Blob) => {
        const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, 'CFEs Emitidos - ' + filtroObtenerCFEsDTO.initialDate.toLocaleDateString().replace(/\//g, '-') + '_' + filtroObtenerCFEsDTO.finalDate.toLocaleDateString().replace(/\//g, '-') + '.xlsx');
      });
  }
}
